import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useInView } from 'react-intersection-observer';
import qs from 'qs';

import { usePendingVolunteers } from 'hooks/volunteers/usePendingVolunteers';

// constants
import { TABLE_HEADERS, TABLE_ITEMS_CLASSNAMES } from 'core/constants';

// functions
import { calculateUserAge, setSearchOrSortQueryString } from 'core/functions';
import { resolveNextSort } from 'core/functions/sort';

// types
import {
	ClaimSort,
	IClaimSort,
	IFullTextSearchClaim,
	INVITED_USER_POSSIBLE_SORT,
	ISearchUserClaimsRequest,
	SortDirection,
	UserClaimResponse,
} from '@joc/api-gateway';

// components
import Loader from 'shared/components/Loader';
import AvatarCell from 'shared/components/Table/CellRenderers/AvatarCell';
import FullNameCell from 'shared/components/Table/CellRenderers/FullNameCell';
import CellDefault from 'shared/components/Table/CellRenderers/CellDefault';
import TableHead from 'shared/components/Table/TableHead';
import TableHeadCell from 'shared/components/Table/TableHead/TableHeadCell';
import TableHeadCellSortable from 'shared/components/Table/TableHead/TableHeadCellSortable';
import TableRow from 'shared/components/Table/TableRow';
import TableMain from 'shared/components/Table/TableMain';
import TableBody from 'shared/components/Table/TableBody';

// styles
import styles from './PendingApprovalTable.module.scss';
import { getListVolunteers } from '../Table/helper';
import InputSearch from 'shared/components/Inputs/InputSearch';

const PendingApprovalTable: FC = () => {
	const history = useHistory();

	const location = useLocation();

	const [sortLocal, setSort] = useState<ClaimSort>();

	const [search, setSearch] = useState('');
	const [isClearSearch, setIsClearSearch] = useState(false);

	const { data, hasNextPage, fetchNextPage, isFetching, isFetched, status, refetch } = usePendingVolunteers(
		search,
		sortLocal
	);

	const { ref, inView } = useInView({
		threshold: 0,
		delay: 200,
	});

	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage().finally();
		}
	}, [inView, hasNextPage, fetchNextPage]);

	const volunteersRecords = useMemo(() => data?.pages?.flatMap(page => page.records) || [], [data]);

	const appendQueryString = (newQueryParams?: IFullTextSearchClaim | IClaimSort, searchBy?: string): void => {
		const searchQuery = setSearchOrSortQueryString(location.search, newQueryParams, searchBy);
		history.push({ search: searchQuery });
	};

	const searchVolunteersChangeHandler = (value: string): void => {
		setSearch(value);
	};

	const handleSortChange = useCallback(
		(sortBy: INVITED_USER_POSSIBLE_SORT, clickedDirection?: SortDirection) => {
			setSort(prevSort => {
				const nextSort = resolveNextSort({
					currentSort: prevSort,
					nextSortBy: sortBy,
					clickedDirection,
					allowClear: true,
				});

				if (!nextSort) {
					const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });
					delete searchParams.sort;
					history.replace({ search: qs.stringify(searchParams) });
					return undefined;
				}

				appendQueryString(nextSort, 'sort');
				return ClaimSort.fromJS(nextSort);
			});
		},
		[history, location.search]
	);

	useEffect(() => {
		const data = qs.parse(location.search, { ignoreQueryPrefix: true }) as unknown as ISearchUserClaimsRequest;
		setSort(data.sort);
	}, []);

	return (
		<div className={styles.container}>
			<div className={styles.table}>
				<InputSearch
					placeholder="Search..."
					classList={['big']}
					changeHandler={searchVolunteersChangeHandler}
					isClearSearch={isClearSearch}
					setIsClearSearch={setIsClearSearch}
				/>
				{volunteersRecords.length ? (
					<TableMain>
						<TableHead callChild="pending-volunteers">
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.avatar}
								text={TABLE_HEADERS.avatar}
								isSortDisabled={true}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.fullName}
								sortBy={INVITED_USER_POSSIBLE_SORT.FirstName}
								handleSortChange={handleSortChange}
								sort={sortLocal}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.email}
								sortBy={INVITED_USER_POSSIBLE_SORT.Email}
								handleSortChange={handleSortChange}
								sort={sortLocal}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.age}
								sortBy={INVITED_USER_POSSIBLE_SORT.BirthDate}
								handleSortChange={handleSortChange}
								sort={sortLocal}
							/>
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.listOrganizations}
								text="Current Organization(s)"
								isSortDisabled={true}
							/>
							<TableHeadCellSortable
								title="Pending Organization"
								sortBy={INVITED_USER_POSSIBLE_SORT.OrgName}
								handleSortChange={handleSortChange}
								sort={sortLocal}
							/>

							{/* <TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} isSortDisabled={true} /> */}
						</TableHead>
						<TableBody>
							{volunteersRecords.map((volunteer: UserClaimResponse) => (
								<TableRow key={volunteer.id} callChild="pending-volunteers">
									<AvatarCell imagePath={volunteer.imagePath} withPopup={true} />
									<FullNameCell firstName={volunteer.firstName} lastName={volunteer.lastName} />
									<CellDefault
										parentClassName={TABLE_ITEMS_CLASSNAMES.email}
										text={volunteer.email}
									/>
									<CellDefault
										parentClassName={TABLE_ITEMS_CLASSNAMES.age}
										text={volunteer.birthDate ? calculateUserAge(volunteer.birthDate) : 'No data'}
									/>
									{volunteer.organizations?.length ? (
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.listOrganizations}
											// TODO: be should implement this
											boldDefaultOrg={!!volunteer.defaultOrganizationId}
											text={getListVolunteers(
												volunteer.organizations.filter(
													org => org.id !== volunteer.organisation?.id
												),
												volunteer.defaultOrganizationId
											)}
										/>
									) : (
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.listOrganizations}
											// TODO: be should implement this
											text="No organizations"
										/>
									)}
									<CellDefault
										parentClassName={TABLE_ITEMS_CLASSNAMES.age}
										text={volunteer.organisation.organizationName}
									/>

									<div className={styles.action_cell}>
										<></>
									</div>
								</TableRow>
							))}
							{!isFetching && (
								<tr style={{ height: 1 }} ref={ref}>
									<td style={{ padding: 0 }}>
										<div style={{ height: '1px', visibility: 'hidden' }} />
									</td>
								</tr>
							)}
						</TableBody>
					</TableMain>
				) : (
					(status === 'pending' || isFetching) && <Loader />
				)}
			</div>
		</div>
	);
};

export default PendingApprovalTable;
